<template>
  <div class="comment-history">
    <template v-if="!hideHeader">
      <FriedH4>{{ t('cc_comment_history') }}</FriedH4>
      <hr />
    </template>
    <div class="controls-container">
      <div class="filter-controls">
        <FriedSelect
          v-model="filter"
          data-external="filter-comments-select"
          class="status-filter"
          :label="t('cc_filter_by')"
          :options="contextualCommentsFilterOptions"
          option-label="label"
          option-key="key"
        />
        <FriedIconButton data-external="sort-comments-button" @click="ascending = !ascending">
          <FriedIcon :icon="ascending ? 'arrow-down-lines' : 'arrow-up-lines'" />
        </FriedIconButton>
      </div>
    </div>
    <transition-group
      v-if="hasFilteredConversationCards"
      class="cards-container"
      name="appear-list"
      tag="span"
    >
      <ConversationCard
        v-for="comment of filteredComments"
        :key="comment.id"
        v-click-outside="() => clickOutside(comment.id)"
        :comment="comment"
        :selected="comment.id === selectedId"
        :hovered="comment.id === hoveredCommentId"
        :current-user="currentUser"
        :recipients="recipients"
        :sender="sender"
        :read-only="readOnly"
        in-comment-history
        @create-reply="$emit('create-reply', $event)"
        @select="$emit('select', comment.id)"
        @hover-change="$event => hoverComment($event ? comment.id : '')"
        @update="$emit('update', { ...$event, commentId: comment.id })"
        @go-to-comment="$emit('go-to-comment', comment.id)"
      />
    </transition-group>
    <div v-else class="no-comments-outer-container">
      <div class="no-comments-inner-container">
        <FriedH2> 🙌 </FriedH2>
        <FriedH5 class="">{{ t('cc_all_clear_here') }} </FriedH5>
        <FriedParagraph class="no-comments-text" :size="ParagraphSize.Small"
          >{{ t('cc_empty_state_description') }}
        </FriedParagraph>
      </div>
    </div>
    <div v-if="comments.length && showHideCommentsToggle" class="hide-comments-container">
      <FriedSwitch
        data-external="signing-page-topbar-hide-comments-menu-item"
        :model-value="commentsHidden"
        :label="t('cc_hide_all_comments_markups')"
        @update:model-value="$emit('toggle-comments')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { ParagraphSize } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { t } from '../../helpers/translation.helper';
import type { Recipient } from '../../types/Recipient';
import type { User } from '../../users/types/user';
import { CommentsHelper } from '../helpers/comments.helper';
import { useContextualCommentingStore } from '../store/contextual-commenting.store';
import { CommentStatus } from '../types/comment-status';
import type { CurrentUser } from '../types/current-user';
import ConversationCard from '../conversation-card/components/ConversationCard.vue';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';

const All = 'All';
const Unresolved = 'Unresolved';
const Resolved = 'Resolved';

type Filter = typeof All | typeof Unresolved | typeof Resolved;

export default defineComponent({
  name: 'CommentHistory',
  components: { ConversationCard },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    selectedId: {
      type: String,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    commentsHidden: {
      type: Boolean,
    },
    showHideCommentsToggle: {
      type: Boolean,
    },
  },
  emits: ['go-to-comment', 'update', 'select', 'create-reply', 'click-outside', 'toggle-comments'],
  setup(props, { emit }) {
    const contextualCommentsFilterOptions = ref([
      { key: All, label: t('cc_all') },
      { key: Unresolved, label: t('cc_unresolved') },
      { key: Resolved, label: t('cc_resolved') },
    ]);

    const ascending = ref(true);
    const filter = ref<Filter>(All);

    const { hoverComment } = useContextualCommentingStore();
    const { hoveredCommentId } = storeToRefs(useContextualCommentingStore());

    const sortedComments = computed(() =>
      CommentsHelper.sortByCreatedAt(props.comments, ascending.value)
    );

    const filteredComments = computed(() => {
      if (filter.value === Unresolved) {
        return sortedComments.value.filter(comment =>
          [CommentStatus.Active, CommentStatus.Draft].includes(comment.status)
        );
      }

      if (filter.value === Resolved) {
        return sortedComments.value.filter(comment => comment.status === CommentStatus.Resolved);
      }

      return sortedComments.value;
    });

    const hasFilteredConversationCards = computed(() => filteredComments.value.length > 0);

    const clickOutside = (commentId: string) => {
      emit('click-outside', commentId);
    };

    return {
      ascending,
      contextualCommentsFilterOptions,
      filter,
      filteredComments,
      hasFilteredConversationCards,
      ParagraphSize,
      hoverComment,
      hoveredCommentId,
      t,
      clickOutside,
    };
  },
});
</script>

<style lang="scss" scoped>
.comment-history {
  max-height: 600px;
  display: flex;
  flex-direction: column;

  hr {
    width: 100%;
    border: 0;
    border-top: 1px solid var(--gray-96);
    margin-bottom: var(--spacing-100);
    margin-top: var(--spacing-100);
  }

  .filter-controls {
    display: flex;
    align-items: flex-end;
    gap: var(--spacing-50);
    width: 100%;

    .status-filter {
      width: 100%;
    }
  }

  .controls-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-100);
    margin-bottom: var(--spacing-100);
  }

  .cards-container,
  .no-comments-outer-container,
  .no-comments-inner-container {
    display: flex;
    flex-direction: column;
  }

  .cards-container {
    gap: var(--spacing-100);
    overflow-y: scroll;

    .mark-all-as-read-container {
      display: flex;
      justify-content: center;
      margin-bottom: var(--spacing-50);
    }
  }

  .no-comments-outer-container {
    height: 100%;
    justify-content: space-around;
    align-items: center;
  }

  .no-comments-inner-container {
    align-items: center;
    gap: var(--spacing-50);
    width: 167px;

    .no-comments-text {
      color: var(--text-gray);
      padding-top: var(--spacing-100);
      text-align: center;
    }
  }
}

.appear-list-enter-to,
.appear-list-leave-from {
  opacity: 1;
}

.appear-list-leave-from {
  max-height: 10000px;
}

.appear-list-enter-active,
.appear-list-leave-active {
  transition:
    max-height 0.6s ease-in-out,
    opacity 0.6s ease-in-out 0.1s;
}

.appear-list-enter-from,
.appear-list-leave-to {
  opacity: 0;
}

.appear-list-leave-to {
  max-height: 0;
}

.appear-list-move {
  transition: transform 0.3s;
}
</style>
